import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../components/Signup/Signup.css';
import { DeclarationsTable } from '../components/DeclarationManagement/DeclarationsTable';
import { ChevronDown, ArrowUpDown, Search } from 'lucide-react';
import { useLanguage, getDeclarationManagementTranslation } from '../hooks/useLanguage';
import { CardStat } from '../components/CardStat';

function DeclarationManagement() {
  const dispatch = useDispatch();
  const groupedTaricJsonOutputs = useSelector((state) => state.groupedTaricJsonOutputs || []);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('expediente-desc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSpanish] = useLanguage();

  // Flatten grouped data: each exportacion becomes a row
  // Use exportacion.id from the exportaciones array as the table ID
  // New structure: exportacion.taric.duas.exportaciones[0] contains the main data
  const flattenedExportaciones = groupedTaricJsonOutputs.flatMap(group => 
    (group.exportaciones || []).map(exportacion => {
      const exportacionData = exportacion.taric?.duas?.exportaciones?.[0] || {};
      return {
        id: exportacion.id, // This is exportaciones[].id (e.g., 725, 724, etc.)
        ocr_id: group.ocr_id, // This is the ocr_input_id (e.g., 821, 819, etc.)
        expediente: exportacionData.expediente || '',
      date_created: exportacion.date_created || '',
        partidas_count: exportacionData.partidas?.length || 0,
        pais_exportacion: exportacionData.pais_exportacion || '',
        pais_destino: exportacionData.pais_destino || '',
        exportacionData: exportacion.taric, // Store full taric object
        exportacion: exportacion // Store full exportacion object with id, taric, date_created
      };
    })
  );

  const sortOptions = [
    { value: 'expediente-desc', label: getDeclarationManagementTranslation('expedienteZA', isSpanish) },
    { value: 'expediente-asc', label: getDeclarationManagementTranslation('expedienteAZ', isSpanish) },
    { value: 'date-desc', label: getDeclarationManagementTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getDeclarationManagementTranslation('oldestFirst', isSpanish) }
  ];

  const filteredDeclarations = flattenedExportaciones.filter(declaration => {
    const hasMatchingExpediente = declaration.expediente?.toLowerCase().includes(searchQuery.toLowerCase());
    const hasMatchingPaisExportacion = declaration.pais_exportacion?.toLowerCase().includes(searchQuery.toLowerCase());
    const hasMatchingPaisDestino = declaration.pais_destino?.toLowerCase().includes(searchQuery.toLowerCase());
    return hasMatchingExpediente || hasMatchingPaisExportacion || hasMatchingPaisDestino;
  });

  const sortedDeclarations = [...filteredDeclarations].sort((a, b) => {
    const aId = a.id || 0;
    const bId = b.id || 0;
    const aExpediente = a.expediente || '';
    const bExpediente = b.expediente || '';
    const aDateCreated = a.date_created || '';
    const bDateCreated = b.date_created || '';

    switch (sortBy) {
      case 'expediente-asc':
        return aExpediente.localeCompare(bExpediente);
      case 'expediente-desc':
        return bExpediente.localeCompare(aExpediente);
      case 'date-asc':
        return new Date(aDateCreated) - new Date(bDateCreated);
      case 'date-desc':
        return new Date(bDateCreated) - new Date(aDateCreated);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || getDeclarationManagementTranslation('sortBy', isSpanish);
  };

  // Function to fetch grouped Taric JSON outputs
  const fetchGroupedTaricJsonOutputs = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/taric_json_output/`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch grouped Taric JSON outputs');
      }

      const data = await response.json();

      // Store in Redux
      dispatch({ type: 'SET_GROUPED_TARIC_JSON_OUTPUTS', payload: data });
    } catch (err) {
      console.error("Error fetching grouped Taric JSON outputs:", err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchGroupedTaricJsonOutputs();
  }, [dispatch]); 

  // Calculate stats from flattened exportaciones
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  let numExportacionesThisMonth = 0;
  let paisDestinoCounts = {};
  
  flattenedExportaciones.forEach(exportacion => {
    // Count exportaciones created this month
    if (exportacion.date_created) {
      const d = new Date(exportacion.date_created);
      if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
        numExportacionesThisMonth++;
      }
    }
    // Pais destino count
    if (exportacion.pais_destino) {
      paisDestinoCounts[exportacion.pais_destino] = (paisDestinoCounts[exportacion.pais_destino] || 0) + 1;
    }
  });
  
  let topPaisDestino = '-';
  let topPaisDestinoCount = 0;
  for (const [pais, count] of Object.entries(paisDestinoCounts)) {
    if (count > topPaisDestinoCount) {
      topPaisDestino = pais;
      topPaisDestinoCount = count;
    }
  }

  return (
    <div className="text-left">
      <h1 className='text-3xl font-bold mb-20'>{getDeclarationManagementTranslation('exportDeclarationManagement', isSpanish)}</h1>
      <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-bottom-gray">
        <CardStat value={flattenedExportaciones.length} title={isSpanish ? 'Exportaciones' : 'Exports'} />
        <CardStat value={numExportacionesThisMonth} title={isSpanish ? 'Exportaciones este mes' : 'Exports This Month'} />
        <CardStat value={topPaisDestino} title={isSpanish ? 'País destino más frecuente' : 'Top Destination Country'} />
      </div>
      <div className='flex justify-between gap-4 mb-4 items-center'>
        <h2 className='text-xl font-bold'>{isSpanish ? 'Lista de declaraciones' : 'Declaration List'}</h2>
        <div className="flex gap-2 items-center">
          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect">
            <button
              className="flex items-center gap-2 px-2 py-2 rounded-full truncate text-sm"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10 text-sm">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Search Input */}
          <div className="relative text-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-64 pl-10 pr-4 py-2 border-container rounded-full text-sm"
              placeholder={getDeclarationManagementTranslation('searchByExpedienteOrPais', isSpanish)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <DeclarationsTable exportaciones={sortedDeclarations} isLoading={isLoading} />
    </div>
  );
}

export default DeclarationManagement;